/**
 * Wire shapes of the Employment admin API (`/admin/listeners/:id/employment`,
 * `/admin/employment/*`). Mirrors maja_api `shared-types/employment.ts`,
 * `employment.types.ts` (termsOut) and `employment-attendance.service.ts`
 * (DayView / summary).
 *
 * Money is whole PAISE as a digit string — and `null` when the admin lacks
 * `listeners.employment.pay.view` (the server redacts; the panel shows "—").
 * Durations are whole seconds; times of day are minutes after midnight in the
 * terms timezone; dates are the employee's work dates as `YYYY-MM-DD`.
 */

export type EmploymentStatus = 'active' | 'paused' | 'ended';
export type EmploymentType = 'full_time' | 'part_time';
export type TermsState = 'active' | 'paused';
/** `livestream` = the listener's own live streams, `calls` = connected calls,
 *  `livestream_calls` = the union (default). `live` / `online` are the older
 *  heartbeat bases (app-open time, callable time). */
export type WorkTimeBasis = 'livestream' | 'calls' | 'livestream_calls' | 'live' | 'online' | 'live_calls' | 'online_calls';
export type BreakMode = 'auto_deduct' | 'none';
export type SalaryModel = 'monthly' | 'weekly' | 'daily' | 'hourly';
export type SalaryAttendanceMode = 'prorated' | 'fixed';
export type OvertimeTrigger = 'disabled' | 'after_scheduled_hours' | 'after_shift_end' | 'daily_threshold';
export type OvertimePayMethod = 'same_rate' | 'fixed_rate' | 'multiplier';
export type DivisorMode = 'period_scheduled_hours' | 'fixed_hours';
export type RoundDirection = 'down' | 'nearest' | 'up';
export type PeriodCapUnit = 'week' | 'month';
export type OffDayRule = 'unpaid' | 'overtime' | 'regular';
export type HolidayPay = 'paid' | 'unpaid';
export type ScheduleType = 'fixed_window' | 'flexible';
/** automatic: the worker credits the wallet; manual: an admin clicks Credit to Wallet. */
export type SettlementMode = 'automatic' | 'manual';
/** end_of_day: one settlement after the freeze; target_completed: regular pay
 *  as soon as the day's target is met, overtime at the end of the day. */
export type SettlementTrigger = 'end_of_day' | 'target_completed';
export type SettlementKind = 'daily' | 'on_target' | 'adjustment';
export type SettlementStatus = 'pending' | 'credited' | 'cancelled';
/** A day's wallet position: nothing / all pending / all credited / both. */
export type DaySettlementStatus = 'none' | 'pending' | 'credited' | 'partial';
export type DayType = 'working' | 'off' | 'holiday' | 'paused';
export type AttendanceStatus =
  | 'present'
  | 'partial'
  | 'absent'
  | 'day_off'
  | 'off_day_worked'
  | 'holiday'
  | 'holiday_worked'
  | 'paused';
export type OvertimeStatus = 'none' | 'auto' | 'pending' | 'approved' | 'rejected';
export type AdjustmentPlacement = 'in_shift' | 'after_shift';
export type HolidayKind = 'holiday' | 'working_day';

export const ATTENDANCE_STATUSES: AttendanceStatus[] = [
  'present',
  'partial',
  'absent',
  'day_off',
  'off_day_worked',
  'holiday',
  'holiday_worked',
  'paused',
];

/** API limits (the server enforces them independently). */
export const EMPLOYMENT_REASON_MIN = 3;
export const EMPLOYMENT_REASON_MAX = 500;
export const EMPLOYMENT_MAX_SALARY_PAISE = 1_000_000_000n;
export const EMPLOYMENT_MAX_OT_RATE_PAISE = 10_000_000n;
export const EMPLOYMENT_MAX_ADJUST_SECONDS = 86_400;

export interface ScheduleDay {
  /** 1 = Monday … 7 = Sunday (ISO). */
  weekday: number;
  working: boolean;
  startMinute: number;
  /** May be <= startMinute: the shift then crosses midnight. */
  endMinute: number;
  breakMinutes: number;
}

/** Overtime block as SENT (money as integers). */
export interface OvertimeInput {
  enabled: boolean;
  trigger: OvertimeTrigger;
  thresholdSeconds: number | null;
  payMethod: OvertimePayMethod;
  ratePaisePerHour: number;
  /** 1.5× = 15000. */
  multiplierBps: number;
  divisorMode: DivisorMode;
  divisorHours: number | null;
  minSeconds: number;
  roundSeconds: number;
  roundDirection: RoundDirection;
  dailyCapSeconds: number | null;
  periodCapSeconds: number | null;
  periodCapUnit: PeriodCapUnit;
  approvalRequired: boolean;
  manualAdjustAllowed: boolean;
  /** Fixed-window schedules only: presence outside the shift window counts as
   *  worked time (so it can become overtime). Omitted for flexible schedules. */
  outsideWindowCounts?: boolean;
}

/** Overtime block as RECEIVED — the two ₹ fields are null without pay.view. */
export type OvertimeOut = Omit<OvertimeInput, 'ratePaisePerHour' | 'multiplierBps'> & {
  ratePaisePerHour: number | null;
  multiplierBps: number | null;
};

/** `terms` of create / new-version / preview requests. */
export interface TermsInput {
  state: TermsState;
  timezone: string;
  dayStartMinute: number;
  weekStartDay: number;
  schedule: ScheduleDay[];
  breakMode: BreakMode;
  workTimeBasis: WorkTimeBasis;
  /** fixed_window: only the per-weekday start/end counts; flexible: `dailyTargetSeconds` anywhere in the work day. */
  scheduleType: ScheduleType;
  /** Required seconds per working day — flexible schedules only (null otherwise). */
  dailyTargetSeconds: number | null;
  presentToleranceSeconds: number;
  salaryModel: SalaryModel;
  /** Digit string of paise. */
  salaryAmountPaise: string;
  salaryAttendanceMode: SalaryAttendanceMode;
  /** No regular pay on a working day unless its target is met. */
  minimumCompletionRequired: boolean;
  overtime: OvertimeInput;
  offDayRule: OffDayRule;
  holidayPay: HolidayPay;
  holidayWorkRule: OffDayRule;
  settlementMode: SettlementMode;
  settlementTrigger: SettlementTrigger;
}

/** One stored terms version (termsOut). */
export interface TermsOut extends Omit<TermsInput, 'salaryAmountPaise' | 'overtime'> {
  id: string;
  versionNo: number;
  effectiveFrom: string;
  isCorrection: boolean;
  salaryAmountPaise: string | null;
  overtime: OvertimeOut;
  changeReason: string;
  createdBy: string;
  createdAt: string;
}

export interface Employment {
  id: string;
  listenerId: string;
  status: EmploymentStatus;
  employmentType: EmploymentType;
  employeeCode: string | null;
  joiningDate: string;
  endDate: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One attendance day (frozen row or today's live figure). */
export interface DayView {
  workDate: string;
  dayType: DayType;
  frozen: boolean;
  termsId: string | null;
  scheduledSeconds: number;
  holidayCreditSeconds: number;
  presenceSeconds: number;
  breakDeductedSeconds: number;
  workedSeconds: number;
  regularSeconds: number;
  overtimeSeconds: number;
  unpaidExtraSeconds: number;
  /** Fixed-window days: presence outside the shift window (paid only when `outsideWindowCounts`). */
  outsideWindowSeconds: number;
  missingSeconds: number;
  adjustmentSeconds: number;
  basePaise: string | null;
  overtimePaise: string | null;
  pendingOvertimePaise: string | null;
  overtimeStatus: OvertimeStatus;
  overtimeApprovedSeconds: number | null;
  status: AttendanceStatus;
  payrollPeriodId: string | null;
  computedAt: string | null;
  /** Wallet position of the day's pay (settlement rows). */
  settlementStatus: DaySettlementStatus;
  /** Σ credited settlements of the day (paise); null when redacted. */
  settledPaise: string | null;
  /** Σ pending settlements of the day (paise); null when redacted. */
  pendingSettlementPaise: string | null;
}

/** GET /admin/listeners/:id/employment */
export interface EmploymentGet {
  employment: Employment | null;
  currentTerms: TermsOut | null;
  /** The newest version, which may be future-dated. Absent for a non-employee. */
  latestTerms?: TermsOut | null;
  today: DayView | null;
  versions: number;
}

/** GET …/employment/summary */
export interface EmploymentSummary {
  from: string;
  to: string;
  days: number;
  scheduledSeconds: number;
  presenceSeconds: number;
  workedSeconds: number;
  regularSeconds: number;
  overtimeSeconds: number;
  unpaidExtraSeconds: number;
  missingSeconds: number;
  pendingOvertimeDays: number;
  basePaise: string | null;
  overtimePaise: string | null;
  pendingOvertimePaise: string | null;
  employmentTotalPaise: string | null;
  /** Credited to the listener wallet in the range. */
  settledPaise: string | null;
  /** Waiting for a wallet credit (manual mode, or the worker's next run). */
  pendingSettlementPaise: string | null;
}

/** POST …/employment/preview */
export interface PreviewResponse {
  sampleDate: string;
  expectedSecondsOnSample: number;
  weeklyScheduledSeconds: number;
  periodScheduledSeconds: number;
  derivedHourlyPaise: string | null;
  samples: Array<{ label: string } & DayView>;
}

export interface Holiday {
  id: string;
  holidayDate: string;
  name: string;
  /** null = every employee. */
  employmentId: string | null;
  kind: HolidayKind;
  createdBy: string;
  createdAt: string;
}

/** GET /admin/employment/badges → { [listenerId]: … } */
export type BadgeMap = Record<string, { status: EmploymentStatus; employmentType: EmploymentType }>;

export interface ReportItem {
  employment: Employment;
  listener: { id: string; displayName: string; userId: string; handle: string };
  terms: {
    salaryModel: SalaryModel;
    workTimeBasis: WorkTimeBasis;
    scheduleType: ScheduleType;
    settlementMode: SettlementMode;
    settlementTrigger: SettlementTrigger;
    timezone: string;
  } | null;
  days: number;
  scheduledSeconds: number;
  presenceSeconds: number;
  workedSeconds: number;
  regularSeconds: number;
  overtimeSeconds: number;
  unpaidExtraSeconds: number;
  missingSeconds: number;
  basePaise: string | null;
  overtimePaise: string | null;
  employmentTotalPaise: string | null;
}

export interface ReportResponse {
  from: string;
  to: string;
  page: number;
  pageSize: number;
  total: number;
  items: ReportItem[];
}

/**
 * One employment settlement: the unit of money that goes from the platform to
 * the listener wallet. `daily` after the day's freeze, `on_target` when the
 * trigger releases regular pay early, `adjustment` for every post-freeze delta
 * (signed — a negative row reverses). Credited at most once.
 */
export interface Settlement {
  id: string;
  employmentId: string;
  workDate: string;
  kind: SettlementKind;
  seq: number;
  /** Signed paise digit string; null when redacted. */
  amountPaise: string | null;
  status: SettlementStatus;
  mode: SettlementMode;
  reason: string | null;
  creditedAt: string | null;
  creditedBy: string | null;
  /** The wallet ledger rows reference this id (set once credited). */
  walletTransactionId: string | null;
  createdAt: string;
}

/** A settlement in a list — who it belongs to. */
export interface SettlementRow extends Settlement {
  listener: { id: string; displayName: string | null; userId: string };
  employmentType: EmploymentType;
}

/** GET /admin/listeners/:id/employment/settlements, GET /admin/employment/settlements */
export interface SettlementListResponse {
  page: number;
  pageSize: number;
  total: number;
  items: SettlementRow[];
}
